import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import type { GetIssueByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Issue_Assessment_Status_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageForm } from 'src/components/form/form/PageForm';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import { useInsertIssueAssessment } from 'src/hooks/mutations/issue-assessment/useInsertIssueAssessment';
import { useUpdateIssueAssessment } from 'src/hooks/mutations/issue-assessment/useUpdateIssueAssessment';
import { useGetIssueAssessmentsByParentId } from 'src/hooks/queries';
import { useGetThirdPartyOptions } from 'src/hooks/queries/third-party/useGetThirdPartyOptions';
import type { IssueAssessmentFields } from 'src/pages/issues/update/forms/issue-assessment-form/issueAssessmentSchema';
import {
  createIssueAssessmentSchema,
  defaultValues,
} from 'src/pages/issues/update/forms/issue-assessment-form/issueAssessmentSchema';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { MUTATION_NO_OP } from '@/hooks/useMutationResultNotification';
import { handleError } from '@/utils/errorUtils';
import { IssueTypeMapping } from '@/utils/issueVariantUtils';

import IssueAssessmentForm from '../../forms/issue-assessment-form/IssueAssessmentForm';
import {
  getAssociatedControlIds,
  getResponsibleThirdPartyTokens,
  mapParentsToIds,
  mapResponsibleThirdPartiesToPayload,
  resolveLinkIdValues,
  resolveRequestedResponsibleThirdPartyIds,
} from './assessmentUtils';
import type { IssueAssessmentRequestedChanges } from './types';

type Props = {
  issue: GetIssueByIdQuery['issue'][number];
  type: ParentIssueType;
};

const Tab: FC<Props> = ({ issue, type }) => {
  const navigate = useNavigate();
  useI18NSummaryHelpContent('issueAssessment.help');
  const issueTypeMapping = IssueTypeMapping[type];
  const { t: st } = useTranslation(['common'], {
    keyPrefix: issueTypeMapping.assessmentTaxonomy,
  });
  const { t } = useTranslation(['common']);
  const issueId = useGetGuidParam('issueId');
  const isThirdPartyModuleEnabled = useIsModuleEnabled('third_party');
  const { data: thirdPartyOptionsData, loading: isThirdPartyOptionsLoading } =
    useGetThirdPartyOptions({
      queryArgs: {},
      shouldSkip: !isThirdPartyModuleEnabled,
    });
  const thirdPartyOptions = thirdPartyOptionsData?.third_party ?? [];
  const {
    data,
    refetch,
    loading: loadingIssueAssessment,
  } = useGetIssueAssessmentsByParentId({
    queryArgs: { parentIssueId: issueId },
  });
  const {
    hasPermission: canCreateIssueAssessment,
    loading: canCreateIssueAssessmentLoading,
  } = useHasPermissionQuery('insert:issue_assessment', issue);
  const {
    hasPermission: canEditIssueAssessment,
    loading: canEditIssueAssessmentLoading,
  } = useHasPermissionQuery('update:issue_assessment', issue);
  const assessment = data?.issue_assessment?.[0];
  const obligationIds =
    data?.issue_parent
      .filter((i) => i.parent?.ObjectType === Parent_Type_Enum.Obligation)
      .map((i) => i.ParentId) ?? [];
  const controlIds = getAssociatedControlIds(data);
  const documentIds =
    data?.issue_parent
      .filter((i) => i.parent?.ObjectType === Parent_Type_Enum.Document)
      .map((i) => i.ParentId) ?? [];
  const unavailableRegisteredLabel = t('unavailableThirdParty');
  const thirdPartyTokens = getResponsibleThirdPartyTokens({
    rawTypedNames: assessment?.ThirdPartyResponsible,
    issueParents: data?.issue_parent ?? [],
    isRegisteredIncluded: isThirdPartyModuleEnabled,
    thirdPartyOptions,
    unavailableRegisteredLabel,
  });
  const registeredThirdPartyIds = mapParentsToIds(
    data?.issue_parent ?? [],
    Parent_Type_Enum.ThirdParty
  ).map(({ value }) => value);
  const issueAssessmentSchema = useMemo(
    () => createIssueAssessmentSchema(assessment?.ThirdPartyResponsible),
    [assessment?.ThirdPartyResponsible]
  );

  const canModify = assessment
    ? canEditIssueAssessment && !canEditIssueAssessmentLoading
    : canCreateIssueAssessment && !canCreateIssueAssessmentLoading;

  const { updateIssueAssessment } = useUpdateIssueAssessment();
  const { insertIssueAssessment } = useInsertIssueAssessment();
  // Retry a materialised row only while this component still targets its issue.
  const materialisedAssessmentRef = useRef<
    { issueId: string; assessmentId: string } | undefined
  >(undefined);
  const currentIssueIdRef = useRef<string | undefined>(issueId);
  currentIssueIdRef.current = issueId;
  useEffect(() => {
    currentIssueIdRef.current = issueId;

    return () => {
      if (currentIssueIdRef.current === issueId) {
        currentIssueIdRef.current = undefined;
      }
    };
  }, [issueId]);
  if (materialisedAssessmentRef.current?.issueId !== issueId) {
    materialisedAssessmentRef.current = undefined;
  }

  const onSave = async (data: IssueAssessmentFields) => {
    const saveIssueId = issueId;
    try {
      // PolicyOwner is cleared at its unwrap site below, so it's omitted here.
      const policyBreachVariables = data.PolicyBreach
        ? {}
        : {
            PoliciesBreached: undefined,
            PolicyOwnerCommentary: undefined,
          };

      const thirdPartyVariables = mapResponsibleThirdPartiesToPayload({
        isCausedByThirdParty: data.IssueCausedByThirdParty,
        isModuleEnabled: isThirdPartyModuleEnabled,
        originalRegisteredIds: registeredThirdPartyIds,
        originalTypedNames: assessment?.ThirdPartyResponsible,
        tokens: data.ThirdPartyResponsible,
      });

      const systemVariables = data.IssueCausedBySystemIssue
        ? {}
        : {
            SystemResponsible: undefined,
          };

      const regulationVariables = data.RegulatoryBreach
        ? {}
        : {
            Rationale: undefined,
            RegulationsBreached: undefined,
            Reportable: undefined,
          };

      // Shared insert/update payload, built once so the two calls can't drift.
      // They only differ by Id + OriginalTimestamp (update) and Status (close).
      const basePayload = {
        ...data,
        ParentIssueId: saveIssueId,
        TagTypeIds:
          data.tags?.map((t) => t.TagTypeId) ||
          issue.tags?.map((t) => t.TagTypeId) ||
          [],
        DepartmentTypeIds:
          data.departments?.map((d) => d.DepartmentTypeId) || [],
        ...thirdPartyVariables,
        ...regulationVariables,
        ...systemVariables,
        ...policyBreachVariables,
        AssociatedControlIds: resolveLinkIdValues(
          data.AssociatedControlIds,
          controlIds
        ),
        RegulationsBreachedIds: resolveLinkIdValues(
          data.RegulationsBreachedIds,
          obligationIds
        ),
        PoliciesBreachedIds: resolveLinkIdValues(
          data.PoliciesBreachedIds,
          documentIds
        ),
        // Unwrap the UserOption pickers to their id, after the spreads so the
        // value stays the narrowed string. Gate PolicyOwner on PolicyBreach:
        // RHF keeps the unmounted field's value, so an unconditional unwrap would
        // resurrect an owner cleared by unchecking PolicyBreach.
        PolicyOwner: data.PolicyBreach ? data.PolicyOwner?.value : undefined,
        CertifiedIndividual: data.CertifiedIndividual?.value,
      };

      const materialisedAssessmentId =
        materialisedAssessmentRef.current?.issueId === saveIssueId
          ? materialisedAssessmentRef.current.assessmentId
          : undefined;
      let currentAssessment = assessment;
      if (!currentAssessment && materialisedAssessmentId) {
        const refreshed = (await refetch())?.data?.issue_assessment;
        currentAssessment = refreshed?.find(
          ({ Id }) => Id === materialisedAssessmentId
        );
        if (!currentAssessment) {
          throw new Error(
            'Failed to reload the issue assessment. Please try again.'
          );
        }
      }

      let savedId: string | undefined;
      let createdEntity;
      if (currentAssessment) {
        const wasMaterialisedHere =
          materialisedAssessmentId === currentAssessment.Id;
        await updateIssueAssessment({
          ...basePayload,
          Id: currentAssessment.Id,
          OriginalTimestamp: currentAssessment.ModifiedAtTimestamp,
        });
        savedId = currentAssessment.Id;
        if (wasMaterialisedHere) {
          createdEntity = {
            id: currentAssessment.Id,
            destination: issueTypeMapping.detailsUrl(saveIssueId),
          };
        }
      } else if (data.Status === Issue_Assessment_Status_Enum.Closed) {
        // The insert path isn't change-request gated, so closing a never-saved
        // assessment directly would skip a configured closure approval.
        // Materialise the row un-closed first, then close it via the gated update.
        const result = await insertIssueAssessment({
          ...basePayload,
          Status: Issue_Assessment_Status_Enum.Pending,
        });
        const materialisedId = result?.insertChildIssueAssessment?.Id;
        if (!materialisedId) {
          throw new Error('Issue assessment id is missing');
        }
        if (currentIssueIdRef.current === saveIssueId) {
          materialisedAssessmentRef.current = {
            issueId: saveIssueId,
            assessmentId: materialisedId,
          };
        }
        const created = (await refetch())?.data?.issue_assessment?.find(
          ({ Id }) => Id === materialisedId
        );
        if (!created) {
          // Within the same issue scope, keep the materialised id so a retry
          // reloads and updates this row rather than issuing another create.
          throw new Error('Failed to close the issue. Please try again.');
        }
        await updateIssueAssessment({
          ...basePayload,
          Id: created.Id,
          OriginalTimestamp: created.ModifiedAtTimestamp,
          Status: Issue_Assessment_Status_Enum.Closed,
        });
        savedId = created.Id;
        createdEntity = {
          id: created.Id,
          destination: issueTypeMapping.detailsUrl(saveIssueId),
        };
      } else {
        const result = await insertIssueAssessment(basePayload);
        savedId = result?.insertChildIssueAssessment?.Id;
        if (!savedId) {
          throw new Error('Issue assessment id is missing');
        }
        if (currentIssueIdRef.current === saveIssueId) {
          materialisedAssessmentRef.current = {
            issueId: saveIssueId,
            assessmentId: savedId,
          };
        }
        createdEntity = {
          id: savedId,
          destination: issueTypeMapping.detailsUrl(saveIssueId),
        };
      }
      try {
        await refetch();
      } catch (error) {
        if (currentIssueIdRef.current === saveIssueId) {
          handleError(error);
        }
      }

      if (currentIssueIdRef.current !== saveIssueId) {
        return MUTATION_NO_OP;
      }

      return { savedIds: savedId ? [savedId] : undefined, createdEntity };
    } catch (error) {
      if (currentIssueIdRef.current !== saveIssueId) {
        return MUTATION_NO_OP;
      }
      throw error;
    }
  };

  const values: IssueAssessmentFields | undefined = assessment
    ? {
        ...defaultValues,
        ...assessment,
        tags: issue.tags || [],
        AssociatedControlIds: controlIds.map((id) => ({ value: id })) ?? [],
        RegulationsBreachedIds:
          obligationIds.map((id) => ({ value: id })) ?? [],
        PoliciesBreachedIds: documentIds.map((id) => ({ value: id })) ?? [],
        ThirdPartyResponsible: thirdPartyTokens,
        CertifiedIndividual: assessment?.CertifiedIndividual
          ? { value: assessment.CertifiedIndividual, type: 'user' }
          : null,
        PolicyOwner: assessment?.PolicyOwner
          ? { value: assessment.PolicyOwner, type: 'user' }
          : null,
      }
    : {
        ...defaultValues,
        tags: issue.tags || [],
      };

  const mapChanges = (changes: IssueAssessmentRequestedChanges) => {
    const requestedParentIds = mapParentsToIds(
      changes.parents,
      Parent_Type_Enum.ThirdParty
    ).map(({ value }) => value);
    const currentIds = mapParentsToIds(
      data?.issue_parent ?? [],
      Parent_Type_Enum.ThirdParty
    ).map(({ value }) => value);
    const registeredThirdPartyIds = resolveRequestedResponsibleThirdPartyIds({
      requestedIds: changes.ThirdPartyResponsibleIds,
      requestedParentIds,
      currentIds,
    });
    const requestedThirdPartyParents = registeredThirdPartyIds.map(
      (parentId) => ({
        IssueId: '',
        ParentId: parentId,
        ParentType: Parent_Type_Enum.ThirdParty,
        parent: { ObjectType: Parent_Type_Enum.ThirdParty },
      })
    );

    return {
      PoliciesBreachedIds: mapParentsToIds(
        changes.parents,
        Parent_Type_Enum.Document
      ),
      AssociatedControlIds: mapParentsToIds(
        changes.parents,
        Parent_Type_Enum.Control
      ),
      RegulationsBreachedIds: mapParentsToIds(
        changes.parents,
        Parent_Type_Enum.Obligation
      ),
      ThirdPartyResponsible: getResponsibleThirdPartyTokens({
        rawTypedNames: changes.ThirdPartyResponsible,
        issueParents: requestedThirdPartyParents,
        isRegisteredIncluded: isThirdPartyModuleEnabled,
        thirdPartyOptions,
        unavailableRegisteredLabel,
      }),
      PolicyOwner: changes.PolicyOwner
        ? { value: changes.PolicyOwner, type: 'user' as const }
        : null,
      CertifiedIndividual: changes?.CertifiedIndividual
        ? { value: changes.CertifiedIndividual, type: 'user' as const }
        : null,
    };
  };

  const mapPreviewedChanges = (
    current: IssueAssessmentFields | undefined,
    incomingChanges: IssueAssessmentRequestedChanges
  ) => {
    return {
      ...defaultValues,
      ...current,
      ...incomingChanges,
      ...mapChanges(incomingChanges),
    };
  };

  const mapRequestedChanges = (
    requestedChanges: IssueAssessmentRequestedChanges
  ) => ({
    ...requestedChanges,
    ...mapChanges(requestedChanges),
  });
  const readOnly = !canModify || loadingIssueAssessment;

  return (
    <>
      <PageForm<IssueAssessmentFields>
        formId={'update-issue-assessment-form'}
        values={values}
        defaultValues={defaultValues}
        i18n={t(issueTypeMapping.assessmentTaxonomy)}
        onSave={onSave}
        onDismiss={() => {
          if (currentIssueIdRef.current === issueId) {
            navigate('..');
          }
        }}
        schema={issueAssessmentSchema}
        readOnly={readOnly}
        header={st('tab_title')}
        parentType={issueTypeMapping.assessmentType}
        approvalConfig={{ object: { Id: assessment?.Id ?? '' } }}
        mapRequestedChanges={mapRequestedChanges}
        mapPreviewedChanges={mapPreviewedChanges}
      >
        <IssueAssessmentForm
          readOnly={readOnly}
          thirdPartyOptions={thirdPartyOptions}
          isThirdPartyOptionsLoading={isThirdPartyOptionsLoading}
          type={type}
        />
      </PageForm>
    </>
  );
};

export default Tab;
